import { computed, Ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore';
import { FILTER_TO_FACETS_MAPPER } from '@domain/mappers/FilterToFacetsMapper';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import { SelectListItemStateType } from '@domain/enums/SelectListItemStateType';
import type { FilterMapper } from '@app/routing/mappers/FilterMapper';
import { FilterType } from '@domain/enums/FilterType';

export const useCheckboxFilter = (mapper: FilterMapper, filterType: FilterType): { items: Ref<ISelectItem[]>; handleSelectionChange: (item: ISelectItem, state: string) => Promise<void>; } => {
	const searchStore = useSearchStore();
	const router = useRouter();
	const { filtersSelection, pageData, portalType } = storeToRefs(searchStore);

	const facetKey = FILTER_TO_FACETS_MAPPER.get(filterType);

	const facetCounts = computed(() => {
		const counts = new Map<string, number>();
		if (!facetKey) return counts;

		const facets = (pageData.value?.filtersFacets ?? {}) as Record<string, { value: string; count: number }[] | undefined>;
		facets[facetKey]?.forEach((facet) => counts.set(facet.value, facet.count));

		return counts;
	});

	const items = computed<ISelectItem[]>(() => {
		const selectedValues = filtersSelection.value.find((filter) => filter.type === filterType)?.values ?? [];
		const selectedIds = new Set(selectedValues.map((value) => value.id));

		return mapper.getAllExtendedFilterValues(portalType.value).map((value) => ({
			value: value.id,
			text: value.name || value.shortName || String(value.id),
			count: facetCounts.value.get(value.id),
			state: selectedIds.has(value.id)
				? SelectListItemStateType.SELECTED
				: SelectListItemStateType.ENABLED
		}));
	});

	const updateFilters = async (valueId: string, shouldSelect: boolean): Promise<void> => {
		const mappedValue = mapper.getExtendedFilterValueById(valueId);
		if (!mappedValue) return;

		const currentSelection = filtersSelection.value.find((filter) => filter.type === filterType);
		const isAlreadySelected = currentSelection?.values.some((value) => value.id === valueId) ?? false;

		if (shouldSelect && isAlreadySelected) return;
		if (!shouldSelect && !isAlreadySelected) return;

		let nextFilters = [...filtersSelection.value];

		if (shouldSelect) {
			if (currentSelection) {
				const updatedSelection = {
					...currentSelection,
					values: [...currentSelection.values, mappedValue]
				};

				nextFilters = nextFilters.map((filter) => filter.type === filterType ? updatedSelection : filter);
			} else {
				nextFilters = [...nextFilters, { type: filterType, values: [mappedValue] }];
			}
		} else if (currentSelection) {
			const remainingValues = currentSelection.values.filter((value) => value.id !== valueId);

			if (remainingValues.length > 0) {
				const updatedSelection = { ...currentSelection, values: remainingValues };
				nextFilters = nextFilters.map((filter) => filter.type === filterType ? updatedSelection : filter);
			} else {
				nextFilters = nextFilters.filter((filter) => filter.type !== filterType);
			}
		}

		searchStore.filtersSelection = nextFilters;
		await searchStore.setRouteFromFilters(router);
		await searchStore.loadPageData(router);
	};

	const handleSelectionChange = async (item: ISelectItem, state: string): Promise<void> => {
		await updateFilters(String(item.value), state === SelectListItemStateType.SELECTED as string);
	};

	return {
		items,
		handleSelectionChange
	};
};
